/**
 * avatar-session.ts
 *
 * Wrapper für das LiveAvatar SDK v0.0.16.
 * Holt Token vom Backend (/api/token), startet die Session,
 * und leitet avatar.transcription Events an den Parser weiter.
 *
 * SDK: @heygen/liveavatar-web-sdk v0.0.16
 * Docs: https://docs.liveavatar.com
 */

import {
  LiveAvatarSession,
  SessionEvent,
  AgentEventsEnum,
} from '@heygen/liveavatar-web-sdk';

import {
  parseAvatarText,
  deduplicateCards,
  type ParseResult,
  type ProductCard,
} from './companion-panel/product-links';

// ============================================================
// Types
// ============================================================

export interface SessionCallbacks {
  /** Wird bei jedem avatar.transcription Event aufgerufen */
  onTranscription: (result: ParseResult) => void;
  /** Wird aufgerufen wenn [HANDOFF] erkannt wird */
  onHandoff: (result: ParseResult) => void;
  /** Wird aufgerufen wenn die Session endet */
  onSessionEnd: () => void;
  /** Wird bei Fehlern aufgerufen */
  onError: (error: Error) => void;
  /** Status-Updates (connecting, connected, speaking, etc.) */
  onStatus?: (status: string) => void;
  /** Wird aufgerufen wenn der User per Mikrofon spricht (Transkription) */
  onUserTranscription?: (text: string) => void;
  /** Avatar hat angefangen zu sprechen */
  onAvatarSpeakStarted?: () => void;
  /** Avatar hat aufgehört zu sprechen */
  onAvatarSpeakEnded?: () => void;
  /**
   * Stream ist bereit (Video-Track subscribed, ans Video-Element attached).
   * Feuert typischerweise mehrere Sekunden bevor `startAvatarSession()` resolved
   * (configureSession etc. läuft danach noch). Caller sollte hier den UI-Switch
   * (Start-Screen → Video) machen, statt auf das Promise-Resolve zu warten.
   */
  onReady?: () => void;
}

export interface AvatarSessionHandle {
  sessionId: string;
  /** Stoppt die Session */
  stop: () => Promise<void>;
  /** Sendet Text an den Avatar (als User-Input) */
  sendText: (text: string) => void;
  /** Unterbricht den Avatar mitten im Sprechen */
  interrupt: () => void;
  /** Mikrofon stummschalten */
  muteMic: () => Promise<void>;
  /** Mikrofon aktivieren */
  unmuteMic: () => Promise<void>;
  /** Ist das Mikrofon stumm? */
  isMicMuted: () => boolean;
  /** Hat der User ein Mikrofon? */
  hasMicrophone: boolean;
  /** Alle bisher angezeigten Produkte (dedupliziert) */
  allProducts: ProductCard[];
  /** Alle bisher angezeigten Blog-Links (dedupliziert) */
  allBlogs: ProductCard[];
  /** Session-Startzeit */
  startedAt: number;
}

// ============================================================
// Token holen
// ============================================================

async function fetchToken(): Promise<{ sessionId: string; accessToken: string }> {
  const res = await fetch('/api/token', { method: 'POST' });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    const msg = [error.error, error.details].filter(Boolean).join(': ');
    throw new Error(`Token-Fehler (${res.status}): ${msg || 'Unbekannt'}`);
  }

  return res.json();
}

// ============================================================
// Session starten
// ============================================================

/**
 * Startet eine LiveAvatar-Session.
 *
 * @param videoElement - HTML Video Element für den Avatar-Stream
 * @param callbacks - Event-Callbacks
 * @returns AvatarSessionHandle mit stop(), sendText() etc.
 */
export async function startAvatarSession(
  videoElement: HTMLVideoElement,
  callbacks: SessionCallbacks,
): Promise<AvatarSessionHandle> {
  const t0 = performance.now();
  const elapsed = () => `${Math.round(performance.now() - t0)}ms`;

  callbacks.onStatus?.('Verbinde mit Avatar...');

  // 1+2. Token-Fetch und Mikrofon-Anfrage PARALLEL — sie sind unabhängig.
  //      Vorher waren sie sequenziell, was bei langsamem Mic-Prompt im iframe
  //      (Safari, dekalin.de) mehrere Sekunden Verzögerung addiert hat.
  const tokenPromise = fetchToken().then(r => {
    console.log(`[avatar] token fetched (${elapsed()})`);
    return r;
  });
  const micPromise = navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then(stream => {
      stream.getTracks().forEach(t => t.stop());
      console.log(`[avatar] mic granted (${elapsed()})`);
      return true;
    })
    .catch(e => {
      console.warn(`[avatar] mic unavailable (${elapsed()}):`, e);
      callbacks.onStatus?.('Kein Mikrofon erkannt – Text-Chat verfügbar');
      return false;
    });

  const [{ sessionId, accessToken }, hasMicrophone] = await Promise.all([
    tokenPromise,
    micPromise,
  ]);

  // 3. LiveAvatarSession erstellen (voiceChat nur wenn Mikrofon vorhanden)
  const avatar = new LiveAvatarSession(accessToken, { voiceChat: hasMicrophone });

  // Track text sent via input to prevent duplicate chat entries
  let lastSentText: string | null = null;

  // State
  const handle: AvatarSessionHandle = {
    sessionId,
    stop: () => avatar.stop(),
    sendText: (text: string) => {
      lastSentText = text;
      avatar.message(text);
    },
    interrupt: () => avatar.interrupt(),
    muteMic: () => avatar.voiceChat.mute(),
    unmuteMic: () => avatar.voiceChat.unmute(),
    isMicMuted: () => avatar.voiceChat.isMuted,
    hasMicrophone,
    allProducts: [],
    allBlogs: [],
    startedAt: Date.now(),
  };

  // 4. Events registrieren

  // Stream bereit → Video-Element verbinden + Caller informieren (UI-Switch).
  // Feuert sobald der LiveKit-Track subscribed ist — i.d.R. Sekunden bevor
  // avatar.start() resolved. Damit kann der Avatar früh sichtbar werden.
  let readyFired = false;
  avatar.on(SessionEvent.SESSION_STREAM_READY, () => {
    console.log(`[avatar] stream ready (${elapsed()})`);
    avatar.attach(videoElement);

    // Erstes Frame messen
    const onLoaded = () => {
      console.log(`[avatar] video metadata loaded (${elapsed()})`);
      videoElement.removeEventListener('loadedmetadata', onLoaded);
    };
    const onPlaying = () => {
      console.log(`[avatar] video playing (${elapsed()})`);
      videoElement.removeEventListener('playing', onPlaying);
    };
    videoElement.addEventListener('loadedmetadata', onLoaded);
    videoElement.addEventListener('playing', onPlaying);

    videoElement.play().catch(() => {
      callbacks.onStatus?.('Klicken Sie auf den Avatar um zu starten');
    });
    callbacks.onStatus?.('Avatar bereit');

    if (!readyFired) {
      readyFired = true;
      callbacks.onReady?.();
    }
  });

  // Avatar-Transkription → parsen
  avatar.on(
    AgentEventsEnum.AVATAR_TRANSCRIPTION,
    (event: { event_id: string; event_type: string; text: string }) => {
      const result = parseAvatarText(event.text);

      // Produkte/Blogs sammeln (dedupliziert)
      if (result.products.length > 0) {
        handle.allProducts = deduplicateCards([...handle.allProducts, ...result.products]);
      }
      if (result.blogs.length > 0) {
        handle.allBlogs = deduplicateCards([...handle.allBlogs, ...result.blogs]);
      }

      // Handoff oder normales Update?
      if (result.hasHandoff) {
        callbacks.onHandoff(result);
      } else {
        callbacks.onTranscription(result);
      }
    },
  );

  // User-Transkription (Mikrofon-Sprache → Text)
  // Skip if this text was already shown via sendText() (text input)
  avatar.on(
    AgentEventsEnum.USER_TRANSCRIPTION,
    (event: { event_id: string; event_type: string; text: string }) => {
      if (lastSentText && event.text.trim() === lastSentText.trim()) {
        lastSentText = null;
        return;
      }
      lastSentText = null;
      callbacks.onUserTranscription?.(event.text);
    },
  );

  // Avatar spricht / hört zu
  let firstSpeakLogged = false;
  avatar.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
    if (!firstSpeakLogged) {
      firstSpeakLogged = true;
      console.log(`[avatar] first speak_started (${elapsed()})`);
    }
    callbacks.onStatus?.('Avatar spricht...');
    callbacks.onAvatarSpeakStarted?.();
  });

  avatar.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
    callbacks.onStatus?.('Avatar hört zu');
    callbacks.onAvatarSpeakEnded?.();
  });

  // Session beendet
  avatar.on(SessionEvent.SESSION_DISCONNECTED, () => {
    callbacks.onSessionEnd();
  });

  // 5. Session starten
  try {
    const tStart = performance.now();
    await avatar.start();
    console.log(`[avatar] session.start() took ${Math.round(performance.now() - tStart)}ms`);
  } catch (err) {
    throw new Error(
      `Avatar-Start fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  console.log(`[avatar] total startup ${elapsed()}`);
  return handle;
}
